export enum TranslationStatus {
  NotTranslated = 751650000,
  AutoTranslated = 751650001,
  HumanReviewed = 751650002,
  TranslationFailed = 751650003,
}

export enum TicketStatusLabel {
  Proposed = "Proposed",
  Active = "Active",
  Resolved = "Resolved",
  Closed = "Closed",
}

export enum TicketTypeLabel {
  Bug = "Bug",
  FeatureRequest = "Feature Request",
  Question = "Question",
}

export enum TicketSeverityLabel {
  Low = "Low",
  Medium = "Medium",
  High = "High",
  Critical = "Critical",
}

export enum TicketCommentAuthor {
  User = "user",
  Admin = "admin",
}

export type TicketData = {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  type: string;
  severity: string;
  status: string;
  tags: string;
  submittedBy: string;
  createdOn: string | undefined;
  firstTriageDate: string | undefined;
  resolvedDate: string | undefined;
  closedDate: string | undefined;
  teamId: string | undefined;
  teamName: string | undefined;
  category: string | undefined;
  subCategory: string | undefined;
};

export type TicketsResponse = {
  tickets: TicketData[];
  translations?: TranslationMap;
};

export type TicketCategory = {
  id: string;
  name: string;
  displayOrder: number | undefined;
};

export type TicketSubCategory = {
  id: string;
  name: string;
  categoryId: string;
  displayOrder: number | undefined;
};

/** Maps "entityName:sourceKey" → translated display text */
export type TranslationMap = Record<string, string>;

export type TicketOptionsResponse = {
  categories: TicketCategory[];
  subCategories: TicketSubCategory[];
  translations?: TranslationMap;
  teams?: TicketTeamData[];
};

export type SubmitTicketRequest = {
  title: string;
  description: string;
  type: number;
  severity: number;
  categoryId?: string;
  subCategoryId?: string;
  tags?: string;
  attachments?: AttachmentInput[];
  accessId?: string;
  teamId?: string;
};

export type PartnerCenterSeller = {
  id: string;
  name: string;
  roles?: string[];
};

export enum TicketingAccessMode {
  Seller = "seller",
  Denied = "denied",
}

export type TicketingAccessInfo = {
  verified: boolean;
  sellers: PartnerCenterSeller[];
  accessMode: TicketingAccessMode;
  loginEmail?: string;
  notificationEmail?: string;
  notificationEmailOptOut?: boolean;
  notificationEmailVerified?: boolean;
  isNewContact?: boolean;
};

export type TicketComment = {
  id: string;
  comment: string;
  translatedComment?: string;
  translationStatus?: number;
  authorType: string;
  authorName?: string;
  createdOn: string;
};

export type TicketAttachment = {
  id: string;
  name: string;
  description: string;
  url: string;
  createdOn: string;
};

export type TicketDetailData = {
  id: string;
  ticketNumber: string;
  title: string;
  description: string;
  type: string;
  severity: string;
  status: string;
  tags: string;
  submittedBy: string;
  teamId: string | undefined;
  teamName: string | undefined;
  createdOn: string | undefined;
  firstTriageDate: string | undefined;
  resolvedDate: string | undefined;
  closedDate: string | undefined;
  resolutionSummary: string | undefined;
  translatedResolutionSummary: string | undefined;
  resolutionTranslationStatus: number | undefined;
  category: string | undefined;
  categoryId: string | undefined;
  subCategory: string | undefined;
  subCategoryId: string | undefined;
  comments: TicketComment[];
  attachments: TicketAttachment[];
};

export type TicketDetailResponse = {
  ticket: TicketDetailData;
};

export type AddCommentRequest = {
  ticketId: string;
  comment: string;
};

export type CommentsResponse = {
  comments: TicketComment[];
};

export type AttachmentInput = {
  url: string;
  description: string;
};

export type UpdateTicketRequest = {
  ticketId: string;
  tags?: string;
  statusCode?: number;
  teamId?: string;
  addAttachments?: AttachmentInput[];
  removeAttachmentIds?: string[];
};

export type TicketTeamData = {
  id: string;
  name: string;
  isFrozen?: boolean;
};

export type TicketTeamAdminData = {
  id: string;
  name: string;
  memberCount: number;
  managers: string[];
  isDefault: boolean;
  isActive: boolean;
  createdOn: string;
};

export type TicketTeamsAdminResponse = {
  teams: TicketTeamAdminData[];
  sellerName: string;
  isAdmin: boolean;
  isManager: boolean;
};

export type DefaultTeamMemberData = {
  contactId: string;
  name: string;
  email: string;
};

export type CreateTeamRequest = {
  accessId: string;
  teamName: string;
  memberContactIds: string[];
};

export type PaginatedTicketsResponse = {
  tickets: TicketData[];
  totalPages: number;
  totalCount: number;
  currentPage: number;
  queryToken?: string;
  translations?: TranslationMap;
  statusCounts?: TicketStatusCounts;
  teams?: TicketTeamData[];
};

export type TicketPaginationParams = {
  accessId: string;
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  filterTypes: number[];
  filterSeverities: number[];
  filterStatuses: number[];
  filterTeamId?: string;
  filterDateFrom?: string;
  filterDateTo?: string;
  filterCategoryId?: string;
  filterSubCategoryId?: string;
  filterTags?: string;
  search: string;
  queryToken?: string;
  includeCounts?: boolean;
};

export type TicketPaginationState = {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  filterTypes: number[];
  filterSeverities: number[];
  search: string;
  queryTokens: Record<number, string>;
};

export const TICKET_PAGE_SIZE = 10;

export type TicketStatusCounts = {
  proposed: number;
  active: number;
  resolved: number;
  closed: number;
  total: number;
};

export type TeamMemberData = {
  contactId: string;
  name: string;
  email: string;
  role: string;
  addedOn: string;
  isActive: boolean;
};

export type TeamManagerData = {
  contactId: string;
  name: string;
  email: string;
  assignedOn: string;
};

export type TeamDetailResponse = {
  teamId: string;
  teamName: string;
  isActive: boolean;
  isDefault: boolean;
  createdOn: string;
  memberCount: number;
  members: TeamMemberData[];
  managers: TeamManagerData[];
  invitations?: TicketingInvitation[];
  currentUserContactId?: string;
};

export type AddTeamMembersRequest = {
  accessId: string;
  teamId: string;
  memberContactIds: string[];
};

export type RenameTeamRequest = {
  accessId: string;
  teamId: string;
  newName: string;
};

export type RemoveTeamMemberRequest = {
  accessId: string;
  teamId: string;
  contactId: string;
};

export type AssignTeamManagerRequest = {
  accessId: string;
  teamId: string;
  contactIds: string[];
};

export type RemoveTeamManagerRequest = {
  accessId: string;
  teamId: string;
  contactId: string;
};

export type CreateInvitationRequest = {
  accessId: string;
  teamId: string;
  loginEmail: string;
  notificationEmail?: string;
};

export enum InvitationStatus {
  Pending = 751650000,
  Accepted = 751650001,
  Expired = 751650002,
  Revoked = 751650003,
}

export type TicketingInvitation = {
  id: string;
  loginEmail: string;
  notificationEmail?: string;
  teamId: string;
  teamName: string;
  invitedBy: string;
  invitedByName: string;
  status: "Pending" | "Accepted" | "Expired" | "Revoked";
  createdDate: string;
  expiresDate: string;
};
